/**
 * CORE-BN — Front-of-House Attention Capacity · settings form model
 *
 * The pure layer beneath the shop-facing settings surface: what a person
 * may configure, in the units a person thinks in, plus the translation to
 * and from the CORE-BI `shop_culture.foh_attention_policy` blob.
 *
 * Kept out of the component so validation is testable without rendering,
 * and so the surface cannot invent its own bounds. Every rule here reads
 * from `FOH_POLICY_BOUNDS`, which is the same record the model enforces
 * at read time. A value this form accepts is a value the model will
 * honour, and a value this form rejects is one the model would have
 * silently dropped back to its default — which is precisely the
 * confusion the constitution's provenance rule exists to prevent.
 *
 * What is deliberately NOT configurable
 * -------------------------------------
 *   - Dwell escalation. That an aged custody item costs more than a
 *     fresh one is doctrine, not preference, and its curve is derived
 *     from thresholds the custody layer already owns.
 *   - The `waiter_present` per-occurrence weight, which the model never
 *     reads (lobby presence is continuous — see `isShopConfigurableClaimWeight`).
 *   - Raw saturation thresholds. A shop tunes *sensitivity* by name;
 *     three unlabelled ratios are an invitation to invert the bands.
 *
 * Units at the boundary
 * ---------------------
 * The form speaks percentages and minutes because people do. The blob
 * stores fractions and minutes because the model does. All conversion
 * happens here, once.
 *
 * Purity: no I/O, no clock, no React.
 */

import {
  patchShopCulture,
  type ShopCultureObject,
  type ShopCulturePatch,
} from '@/lib/shopCulture/shopCulturePatch';
import {
  FOH_ATTENTION_POLICY_CULTURE_KEY,
  DEFAULT_FOH_CLAIM_WEIGHT_MINUTES,
  DEFAULT_FOH_PERSON_EFFECTIVE_ATTENTION_FRACTION,
  DEFAULT_FOH_SATURATION_THRESHOLDS,
  DEFAULT_FOH_WAITER_PRESENCE_WINDOW_FRACTION,
  DEFAULT_FOH_WINDOW_MINUTES,
  FOH_CONFIGURABLE_CLAIM_KINDS,
  FOH_POLICY_BOUNDS,
  type FohAttentionPolicy,
} from './policy';
import type { FohAttentionClaimKind } from './types';

// =============================================================================
// Saturation sensitivity
// =============================================================================

/**
 * How early the shop wants to hear that its front of house is filling
 * up.
 *
 * Named rather than numeric on purpose. The underlying thresholds are
 * ratios of consumed-to-available attention, and a settings screen that
 * asked an owner for three ascending ratios would mostly collect
 * mistakes. Every preset keeps `over capacity` at 1.0 — being over
 * capacity is an arithmetic fact, not a preference.
 */
export type FohSaturationSensitivity = 'early' | 'balanced' | 'late';

export const FOH_SENSITIVITY_PRESETS: Readonly<
  Record<
    FohSaturationSensitivity,
    Readonly<{ loaded: number; saturated: number; overCapacity: number }>
  >
> = Object.freeze({
  // Warn while there is still real room to act.
  early: Object.freeze({ loaded: 0.55, saturated: 0.8, overCapacity: 1 }),
  // The conservative default.
  balanced: Object.freeze({
    loaded: DEFAULT_FOH_SATURATION_THRESHOLDS.loaded,
    saturated: DEFAULT_FOH_SATURATION_THRESHOLDS.saturated,
    overCapacity: DEFAULT_FOH_SATURATION_THRESHOLDS.overCapacity,
  }),
  // For shops that run deliberately hot and only want the late signal.
  late: Object.freeze({ loaded: 0.85, saturated: 0.95, overCapacity: 1 }),
});

export const FOH_SENSITIVITY_LABELS: Readonly<
  Record<FohSaturationSensitivity, { label: string; description: string }>
> = Object.freeze({
  early: {
    label: 'Tell me early',
    description:
      'Flags a busy front desk while there is still time to add a person or space out arrivals.',
  },
  balanced: {
    label: 'Balanced',
    description:
      'The OptiBay default. Warns before the front desk is full, without commenting on an ordinary busy morning.',
  },
  late: {
    label: 'Only when it is serious',
    description:
      'For shops that run deliberately busy and only want to hear about it near the limit.',
  },
});

/**
 * Recognise which preset a resolved policy corresponds to, so a shop
 * that saved "Balanced" sees "Balanced" selected when it comes back.
 * Falls back to `'balanced'` for any thresholds that match no preset.
 */
export function sensitivityFromThresholds(
  thresholds: Readonly<{ loaded: number; saturated: number; overCapacity: number }>,
): FohSaturationSensitivity {
  for (const key of Object.keys(FOH_SENSITIVITY_PRESETS) as FohSaturationSensitivity[]) {
    const p = FOH_SENSITIVITY_PRESETS[key];
    if (
      p.loaded === thresholds.loaded &&
      p.saturated === thresholds.saturated &&
      p.overCapacity === thresholds.overCapacity
    ) {
      return key;
    }
  }
  return 'balanced';
}

// =============================================================================
// Plain-language labels
// =============================================================================

/**
 * How each configurable claim is described to a shop owner.
 *
 * No implementation vocabulary: no "claim", no "weight", no "custody
 * state". Each row asks a question the person can answer from their own
 * morning.
 */
export const FOH_CLAIM_FORM_LABELS: Readonly<
  Record<FohAttentionClaimKind, { label: string; help: string }>
> = Object.freeze({
  arrival_intake: {
    label: 'Greeting a customer dropping off',
    help: 'Meeting them, checking the vehicle in, hearing the concern and setting expectations.',
  },
  customer_communication: {
    label: 'A call or message with a customer',
    help: 'One phone call, text or email — including the time it takes to pick the thread back up.',
  },
  decision_approval: {
    label: 'Getting work approved',
    help: 'Reaching the customer, explaining the work and getting a yes or no.',
  },
  custody_pending_accept: {
    label: 'A ticket waiting for the front desk',
    help: 'A ticket sitting in the queue until somebody picks it up. Waiting longer costs more.',
  },
  custody_with_advisor: {
    label: 'A ticket being worked at the front desk',
    help: 'A ticket actively on the desk — writing, pricing, ordering, chasing.',
  },
  custody_pending_tech_accept: {
    label: 'A ticket handed back to a technician',
    help: 'Mostly the technician’s job from here, so it costs the front desk very little.',
  },
  technician_support: {
    label: 'A technician asking a question at the desk',
    help: 'Not currently recorded by OptiBay, so this does not yet affect the estimate.',
  },
  waiter_present: {
    label: 'A customer waiting in the lobby',
    help: 'Tuned separately — see the lobby setting.',
  },
});

// =============================================================================
// The form
// =============================================================================

export interface FohAttentionPolicyForm {
  /**
   * Stated front-of-house headcount. `null` means "count who is actually
   * clocked in", which is the better answer when the shop uses the time
   * clock.
   */
  staffHeadcountOverride: number | null;
  /** Percentage, 25–100. What share of a desk person's time is really available. */
  deskTimeAvailablePercent: number;
  /** Percentage, 1–100. What share of one person a lobby customer occupies. */
  lobbyCustomerAttentionPercent: number;
  /** Minutes, 5–480. How far ahead the estimate looks. */
  windowMinutes: number;
  sensitivity: FohSaturationSensitivity;
  /** Minutes per occurrence, keyed by claim. Excludes `waiter_present`. */
  claimMinutes: Record<string, number>;
}

export function defaultFohAttentionPolicyForm(): FohAttentionPolicyForm {
  const claimMinutes: Record<string, number> = {};
  for (const kind of FOH_CONFIGURABLE_CLAIM_KINDS) {
    claimMinutes[kind] = DEFAULT_FOH_CLAIM_WEIGHT_MINUTES[kind];
  }
  return {
    staffHeadcountOverride: null,
    deskTimeAvailablePercent: Math.round(
      DEFAULT_FOH_PERSON_EFFECTIVE_ATTENTION_FRACTION * 100,
    ),
    lobbyCustomerAttentionPercent: Math.round(
      DEFAULT_FOH_WAITER_PRESENCE_WINDOW_FRACTION * 100,
    ),
    windowMinutes: DEFAULT_FOH_WINDOW_MINUTES,
    sensitivity: 'balanced',
    claimMinutes,
  };
}

/** Project a resolved policy back into the form a person edits. */
export function policyToForm(policy: FohAttentionPolicy): FohAttentionPolicyForm {
  const claimMinutes: Record<string, number> = {};
  for (const kind of FOH_CONFIGURABLE_CLAIM_KINDS) {
    claimMinutes[kind] = policy.claimWeightMinutes[kind];
  }
  return {
    staffHeadcountOverride: policy.staffHeadcountOverride,
    deskTimeAvailablePercent: Math.round(
      policy.personEffectiveAttentionFraction * 100,
    ),
    lobbyCustomerAttentionPercent: Math.round(
      policy.waiterPresenceWindowFraction * 100,
    ),
    windowMinutes: policy.windowMinutes,
    sensitivity: sensitivityFromThresholds(policy.saturationThresholds),
    claimMinutes,
  };
}

// =============================================================================
// Validation
// =============================================================================

/** Field-keyed errors. Empty object means the form is safe to save. */
export type FohPolicyFormErrors = Record<string, string>;

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

/**
 * Validate a form against the SAME bounds the model enforces.
 *
 * The zero rules are the interesting ones, and they are stated to the
 * person rather than silently applied: a shop may say a claim is cheap,
 * but not that it is free. A settings screen that accepted zero and then
 * quietly reverted to the default would leave an owner believing they had
 * switched something off.
 */
export function validateFohAttentionPolicyForm(
  form: FohAttentionPolicyForm,
): FohPolicyFormErrors {
  const errors: FohPolicyFormErrors = {};
  const B = FOH_POLICY_BOUNDS;

  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;

  const head = form.staffHeadcountOverride;
  if (head !== null && head !== undefined) {
    const h = num(head);
    if (
      h === null ||
      h < B.staffHeadcountOverride.min ||
      h > B.staffHeadcountOverride.max ||
      !Number.isInteger(h)
    ) {
      errors.staffHeadcountOverride = `Enter a whole number between ${B.staffHeadcountOverride.min} and ${B.staffHeadcountOverride.max}, or leave it blank to count who is clocked in.`;
    }
  }

  const desk = num(form.deskTimeAvailablePercent);
  if (
    desk === null ||
    desk < B.personEffectiveAttentionFraction.min * 100 ||
    desk > B.personEffectiveAttentionFraction.max * 100
  ) {
    errors.deskTimeAvailablePercent = `Enter a percentage between ${pct(B.personEffectiveAttentionFraction.min)} and ${pct(B.personEffectiveAttentionFraction.max)}. Nobody is available more than all of their time.`;
  }

  const lobby = num(form.lobbyCustomerAttentionPercent);
  if (
    lobby === null ||
    lobby < B.waiterPresenceWindowFraction.min * 100 ||
    lobby > B.waiterPresenceWindowFraction.max * 100
  ) {
    errors.lobbyCustomerAttentionPercent =
      lobby !== null && lobby <= 0
        ? 'A customer waiting in the lobby always takes some attention, so this cannot be zero.'
        : `Enter a percentage between ${pct(B.waiterPresenceWindowFraction.min)} and ${pct(B.waiterPresenceWindowFraction.max)}.`;
  }

  const win = num(form.windowMinutes);
  if (win === null || win < B.windowMinutes.min || win > B.windowMinutes.max) {
    errors.windowMinutes = `Enter between ${B.windowMinutes.min} and ${B.windowMinutes.max} minutes.`;
  }

  if (!FOH_SENSITIVITY_PRESETS[form.sensitivity]) {
    errors.sensitivity = 'Choose one of the listed options.';
  }

  for (const kind of FOH_CONFIGURABLE_CLAIM_KINDS) {
    const v = num(form.claimMinutes?.[kind]);
    if (v === null || v < B.claimWeightMinutes.min || v > B.claimWeightMinutes.max) {
      const label = FOH_CLAIM_FORM_LABELS[kind]?.label ?? kind;
      errors[`claimMinutes.${kind}`] =
        v !== null && v <= 0
          ? `“${label}” always takes some attention, so it cannot be zero. The lowest allowed is ${B.claimWeightMinutes.min} minutes.`
          : `Enter between ${B.claimWeightMinutes.min} and ${B.claimWeightMinutes.max} minutes.`;
    }
  }

  return errors;
}

export function isFohPolicyFormValid(form: FohAttentionPolicyForm): boolean {
  return Object.keys(validateFohAttentionPolicyForm(form)).length === 0;
}

// =============================================================================
// Serialisation
// =============================================================================

/**
 * Convert a validated form into the `foh_attention_policy` blob stored
 * under CORE-BI shop culture.
 *
 * Round-trip safe by construction: `resolveFohAttentionPolicy` on this
 * output reproduces the form's values, so what an owner sees after
 * saving is what the model is actually using. Callers must validate
 * first — this function serialises, it does not sanitise, and the model
 * would drop an out-of-range value back to its default.
 */
export function formToPolicyBlob(
  form: FohAttentionPolicyForm,
): Record<string, unknown> {
  const thresholds = FOH_SENSITIVITY_PRESETS[form.sensitivity]
    ?? FOH_SENSITIVITY_PRESETS.balanced;

  const claim_weight_minutes: Record<string, number> = {};
  for (const kind of FOH_CONFIGURABLE_CLAIM_KINDS) {
    claim_weight_minutes[kind] = form.claimMinutes[kind];
  }

  const blob: Record<string, unknown> = {
    claim_weight_minutes,
    saturation_thresholds: {
      loaded: thresholds.loaded,
      saturated: thresholds.saturated,
      over_capacity: thresholds.overCapacity,
    },
    person_effective_attention_fraction: form.deskTimeAvailablePercent / 100,
    waiter_presence_window_fraction: form.lobbyCustomerAttentionPercent / 100,
    window_minutes: form.windowMinutes,
  };

  // Absent means "observe it", which is different from a stated zero.
  if (form.staffHeadcountOverride !== null && form.staffHeadcountOverride !== undefined) {
    blob.foh_staff_headcount = form.staffHeadcountOverride;
  }

  return blob;
}

/** The single `shop_culture` key CORE-BN owns and may state. */
export const FOH_OWNED_CULTURE_KEYS: ReadonlyArray<string> = Object.freeze([
  FOH_ATTENTION_POLICY_CULTURE_KEY,
]);

/**
 * CORE-BI — the patch CORE-BN sends to the server-side `patch_shop_culture`:
 * exactly the one key it owns. `mergeFohPolicyIntoShopCulture` below is the
 * same patch applied locally (kept for its pure tests).
 */
export function fohPolicyCulturePatch(form: FohAttentionPolicyForm): ShopCulturePatch {
  return { [FOH_ATTENTION_POLICY_CULTURE_KEY]: formToPolicyBlob(form) };
}

/** CORE-BI — "reset to OptiBay defaults" as a server patch: remove the one key. */
export const FOH_POLICY_REMOVAL_PATCH: ShopCulturePatch = Object.freeze({
  [FOH_ATTENTION_POLICY_CULTURE_KEY]: undefined,
});

/**
 * Merge the policy blob into an existing `shop_culture` object without
 * disturbing anything else in it.
 *
 * Delegates to the shared `patchShopCulture` authority rather than doing
 * its own spread, so there is one merge rule in the codebase and not
 * several subtly different ones.
 */
export function mergeFohPolicyIntoShopCulture(
  existingCulture: unknown,
  form: FohAttentionPolicyForm,
): ShopCultureObject {
  return patchShopCulture(existingCulture, {
    [FOH_ATTENTION_POLICY_CULTURE_KEY]: formToPolicyBlob(form),
  });
}

/**
 * Return the shop to the untuned model by REMOVING the policy key.
 *
 * The semantics are deliberate and worth stating, because two plausible
 * readings of "reset to defaults" differ in a way the constitution cares
 * about.
 *
 * Writing the default VALUES back would leave the shop permanently marked
 * as having configured its policy: `resolveFohAttentionPolicy` reports a
 * supplied-and-accepted value as `'shop'` provenance regardless of
 * whether it happens to equal the default. The capacity card would then
 * stop disclosing "using OptiBay default weights" for a shop that is, in
 * every meaningful sense, untuned — and that disclosure is a
 * constitutional requirement, not a nicety.
 *
 * Removing the key restores genuinely untuned state, so the shop reads as
 * `usingAllDefaults` again. It removes exactly one key and cannot touch
 * any other capability's configuration.
 */
export function removeFohPolicyFromShopCulture(
  existingCulture: unknown,
): ShopCultureObject {
  return patchShopCulture(existingCulture, {
    // `undefined` is the patch vocabulary for "delete this key".
    [FOH_ATTENTION_POLICY_CULTURE_KEY]: undefined,
  });
}
